import React, { useState, useRef, useEffect } from "react";
import Spinner from "./Spinner";

interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  options: Option[];
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  isLoading?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select",
  label = "Select",
  isLoading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);
  // If "None" is selected (empty string), show placeholder instead
  const displayText = selectedOption && selectedOption.value !== "" 
    ? selectedOption.label 
    : placeholder;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    const nextValue = value === optionValue ? "" : optionValue;
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <div className="w-full relative mt-[6px]" ref={dropdownRef}>
      <div
        className="flex items-center justify-between px-[16px] bg-white border border-[#F1F1F2] h-[42px] rounded-[8px] cursor-pointer hover:border-gray-400 transition-colors text-[#252525]"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span
          className={`text-xs ${value ? "text-[#252525]" : "text-[#9B9B9B]"}`}
        >
          {displayText}
        </span>
        <svg
          className={`w-4 h-4 text-[#9B9B9B] transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-[8px] border border-[#F1F1F2] dropdown-shadow max-h-80 overflow-y-auto w-full">
          <div className="p-2">
            {isLoading ? (
             <div className="my-5"><Spinner /></div>
            ) : (
              <div className="space-y-1">
                {options.length > 0 ? (
                  options.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => handleSelect(option.value)}
                      className={`px-[16px] py-2 text-xs cursor-pointer rounded-[8px] transition-colors font-family: Geist, sans-serif ${
                        value === option.value
                          ? "bg-[#FD76571A] text-[#252525] font-medium"
                          : "text-[#252525] hover:bg-gray-100"
                      }`}
                    >
                      {option.label}
                    </div>
                  ))
                ) : (
                  <div className="px-[16px] py-2 text-sm rounded-[8px] text-[#9B9B9B] transition-colors bg-gray-100 text-gray-900 hover:bg-gray-100">
                    No options found
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
